import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt, FaCalendarAlt, FaMapMarkerAlt, FaBuilding, FaSpinner, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader';
import { FullPageSkeleton } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import {
  usePortalContent,
  getPortalIcon,
  getAnnualReportPageMeta,
  getAnnualReportStepTitle,
  getAnnualReportStepInstruction,
  buildDefaultAnnualReportSteps,
  getAnnualReportChecklist,
  getChecklistProgressFields,
} from '../../portal';
import CmsChecklistForm from './CmsChecklistForm';
import AnnualReportPDFPreviewModal from './AnnualReportPDFPreviewModal';
import { useRealtimeRefresh } from '../../context/RealtimeSyncContext';
import { generateAnnualReportPDF } from './pdfGenerator';

const AnnualReportService = () => {
  const navigate = useNavigate();
  const { content: portalContent, loading: portalLoading } = usePortalContent('annual-report');
  const meta = useMemo(() => getAnnualReportPageMeta(portalContent), [portalContent]);
  const PageIcon = getPortalIcon(meta.pageIcon);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [annualReportData, setAnnualReportData] = useState(null);
  const [annualReportService, setAnnualReportService] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfDataUri, setPreviewPdfDataUri] = useState('');
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fetchAnnualReportData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const data = await userResponse.json();
      setUserData(data.user);

      // Set annual report service data from user data
      if (data.user?.annualReportService) {
        setAnnualReportService(data.user.annualReportService);
      }

      const annualReportResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/${data.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!annualReportResponse.ok) {
        if (annualReportResponse.status === 404) {
          setAnnualReportData(null);
          return;
        }
        throw new Error('Failed to fetch annual report data');
      }

      const arData = await annualReportResponse.json();
      setAnnualReportData(arData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAnnualReportData();
  }, [fetchAnnualReportData]);

  useRealtimeRefresh(['annual-report', 'profile'], fetchAnnualReportData);

  const handleStepClick = (step) => {
    if (step === 1) {
      const step1 = userData?.annualReportSteps?.find(s => s.step === 1);
      if (step1?.status === 'submitted') {
        toast.warning(meta.toasts.alreadySubmitted || 'Checklist has already been submitted.');
        return;
      }
      setShowChecklistForm(true);
    }
  };

  const generateChecklistPdf = (formData) => {
    const checklist = getAnnualReportChecklist(meta, annualReportService);
    return generateAnnualReportPDF({
      formData,
      checklist,
      service: annualReportService,
      userName: userData?.name || '',
    });
  };

  const buildSubmittedSteps = (formData) => {
    const existing = userData?.annualReportSteps || buildDefaultAnnualReportSteps(meta);
    const step2Title = getAnnualReportStepTitle(meta, 2);
    return existing.map((s) => {
      if (s.step === 1) {
        return { ...s, status: 'submitted', answers: formData, submittedAt: new Date().toISOString() };
      }
      if (s.step === 2) {
        return { ...s, title: s.title || step2Title, status: 'in-progress' };
      }
      return s;
    });
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    setPreviewPdfDataUri('');
    setPendingFormData(null);
  };

  const handleOpenPdfPreview = (formData) => {
    setIsGeneratingPreview(true);
    try {
      const pdfBase64 = generateChecklistPdf(formData);
      setPendingFormData(formData);
      setPreviewPdfDataUri(pdfBase64);
      setShowPdfPreview(true);
    } catch (err) {
      toast.error((meta.toasts.submitErrorPrefix || 'Error: ') + err.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleConfirmChecklistSubmit = async () => {
    if (!pendingFormData || !userData?._id) return;

    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      const pdfBase64 = previewPdfDataUri || generateChecklistPdf(pendingFormData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/submit-checklist/${userData._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ formData: pendingFormData, pdfBase64 }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to submit checklist');
      }

      const data = await response.json();
      setUserData((prev) => ({
        ...prev,
        annualReportSteps: data.annualReportSteps || buildSubmittedSteps(pendingFormData),
      }));
      handleClosePdfPreview();
      setShowChecklistForm(false);
      toast.success(meta.toasts.submitSuccess || 'Checklist submitted successfully! A copy has been saved to your Documents.');
      fetchAnnualReportData();
    } catch (err) {
      toast.error((meta.toasts.submitErrorPrefix || 'Error: ') + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveChecklist = async (formData, isFinal = true) => {
    if (!userData?._id) {
      toast.error('User session expired. Please refresh and try again.');
      return;
    }

    if (isFinal) {
      handleOpenPdfPreview(formData);
      return;
    }

    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/steps/${userData._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ draftAnswers: formData }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to save draft');
      }

      const data = await response.json();
      setUserData((prev) => ({
        ...prev,
        annualReportSteps: data.annualReportSteps || prev?.annualReportSteps,
      }));

      toast.success(meta.toasts.draftSaved || 'Draft saved successfully!');
    } catch (err) {
      toast.error((meta.toasts.submitErrorPrefix || 'Error: ') + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepStatus = (step) => {
    if (!userData?.annualReportSteps) return step === 1 ? 'in-progress' : 'pending';

    const steps = userData.annualReportSteps;
    const current = steps.find((s) => s.step === step);
    const step1 = steps.find((s) => s.step === 1);
    const step1Done = step1?.status === 'submitted' || step1?.status === 'completed';

    if (step === 2 && step1Done) {
      const status = current?.status;
      if (status === 'submitted' || status === 'completed') return status;
      return 'in-progress';
    }

    return current?.status || (step === 1 ? 'in-progress' : 'pending');
  };

  const getStepProgress = (step) => {
    if (step !== 1 || !userData?.annualReportSteps) return { progress: 0, total: 15 };
    const s = userData.annualReportSteps.find((item) => item.step === step);
    if (!s || !s.answers) return { progress: 0, total: 15 };

    const checklist = getAnnualReportChecklist(meta, annualReportService);
    const fields = getChecklistProgressFields(checklist);
    const answeredCount = fields.filter((f) => s.answers[f] && s.answers[f].toString().trim() !== '').length;
    return { progress: answeredCount, total: fields.length || 15 };
  };

  const StepCard = ({ step, title, status, onClick, progressData }) => {
    const isCompleted = status === 'submitted' || status === 'completed';
    const isInProgress = status === 'in-progress';
    const isPending = status === 'pending';

    const getCardStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-[#1e2a3e] border-[#2d3f5a]';
        case 'client-pending':
          return 'bg-orange-600 border-orange-500';
        case 'submitted':
        case 'completed':
          return 'bg-green-600 border-green-500';
        default:
          return 'bg-blue-600 border-blue-500';
      }
    };

    const getBadgeStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-blue-600 text-white';
        case 'client-pending':
          return 'bg-orange-800 text-white';
        case 'submitted':
        case 'completed':
          return 'bg-green-800 text-white';
        default:
          return 'bg-blue-800 text-white';
      }
    };

    const getStatusBadge = () => {
      switch (status) {
        case 'in-progress':
          return meta.statusBadges.inProgress;
        case 'client-pending':
          return meta.statusBadges.clientPending;
        case 'submitted':
        case 'completed':
          return meta.statusBadges.completed;
        default:
          return meta.statusBadges.pending;
      }
    };

    const getStepInstructions = () => {
      const cardConfig = meta.stepCards.find((c) => c.step === step);
      if (cardConfig?.instructions) return cardConfig.instructions;
      return getAnnualReportStepInstruction(meta, step, annualReportService);
    };

    return (
      <div 
        onClick={() => onClick(step)}
        className={`group relative w-full p-4 sm:p-5 rounded-2xl border cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full ${getCardStyles()}`}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-white font-bold text-base sm:text-lg">Step {step}</span>
          <span className={`px-2.5 sm:px-3 py-1 rounded text-xs sm:text-xs font-bold uppercase tracking-wide ${getBadgeStyles()}`}>
            {getStatusBadge()}
          </span>
        </div>

        <h3 className="text-white font-semibold text-sm sm:text-base leading-snug flex-grow">
          {title}
        </h3>

        {/* Progress Bar for Step 1 — only while in progress */}
        {step === 1 && status === 'in-progress' && progressData && (
          <div className="mt-auto pt-3">
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span className="tracking-wide">{meta.stepLabels.progress}</span>
              <span className="font-semibold">{progressData.progress}/{progressData.total} • {Math.round((progressData.progress / progressData.total) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-gray-400"
                style={{ width: `${(progressData.progress / progressData.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {isInProgress && step !== 1 && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-300">
            <FaSpinner className="w-3 h-3 animate-spin" />
            <span>{step === 2 ? meta.stepLabels.workingForEmployee : meta.stepLabels.actionRequired}</span>
          </div>
        )}

        {isCompleted && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-green-100">
            <FaCheckCircle className="w-3 h-3" />
            <span>{meta.stepLabels.completed}</span>
          </div>
        )}

        {isPending && (
          <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-100">
            <FaSpinner className="w-3 h-3 animate-spin" />
            <span>{meta.stepLabels.workingForEmployee}</span>
          </div>
        )}

        {/* Tooltip on Hover */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-dark-800 dark:bg-dark-700 text-white text-xs sm:text-sm rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal text-center sm:text-left max-w-[min(280px,80vw)] z-10">
          <span className="block">{getStepInstructions()}</span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-dark-800 dark:border-t-dark-700" />
        </div>
      </div>
    );
  };

  if (loading || portalLoading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message={meta.loadingMessage} />
      </div>
    );
  }

  if (showChecklistForm) {
    const checklist = getAnnualReportChecklist(meta, annualReportService);
    return (
      <div className="animate-fade-in">
        <CmsChecklistForm
          key={`checklist-${userData?._id}-${userData?.annualReportSteps?.find((s) => s.step === 1)?.status || 'new'}`}
          checklist={checklist}
          shell={meta.checklistShell}
          initialData={userData?.annualReportSteps?.find((s) => s.step === 1)?.answers}
          onSave={handleSaveChecklist}
          onCancel={() => {
            handleClosePdfPreview();
            setShowChecklistForm(false);
          }}
          isSubmitting={isSubmitting || isGeneratingPreview}
        />
        {showPdfPreview && (
          <AnnualReportPDFPreviewModal
            pdfDataUri={previewPdfDataUri}
            title={meta.checklistShell?.pdfPreviewTitle || 'Review Your Checklist'}
            subtitle={meta.checklistShell?.pdfPreviewSubtitle || 'Please review the details below before final submission'}
            backLabel={meta.checklistShell?.pdfPreviewBackLabel || 'Back to Edit'}
            confirmLabel={meta.checklistShell?.pdfPreviewConfirmLabel || 'Confirm & Submit'}
            onClose={handleClosePdfPreview}
            onConfirm={handleConfirmChecklistSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <PageHeader
        icon={PageIcon}
        title={meta.pageTitle}
        subtitle={meta.pageSubtitle}
      />

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FaExclamationCircle className="w-5 h-5" />
            <span><strong>{meta.errorLabel}</strong> {error}</span>
          </div>
          <button
            onClick={fetchAnnualReportData}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? meta.errorRefreshingLabel : meta.errorRetryLabel}
          </button>
        </div>
      )}

      {/* Annual Report Due Date Card */}
      {annualReportService?.dueDate && (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}>
          <div
            className="py-2.5 px-4 text-white flex items-center gap-3"
            style={{ background: `linear-gradient(135deg, #4f46e5, #9333ea)` }}
          >
            <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
              <FaCalendarAlt className="w-3.5 h-3.5" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-tight leading-none">{meta.dueDateCardTitle}</h2>
            </div>
          </div>

          <div className="p-2.5 md:p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 md:gap-3">
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaMapMarkerAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">{meta.labelState}</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.state}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaBuilding className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">{meta.labelEntityType}</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.entityType}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaCalendarAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">{meta.labelFormationDate}</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{formatDateForDisplay(annualReportService.registrationDate)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaCalendarAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">{meta.labelFilingYear}</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.filingYear || 'N/A'}</p>
                </div>
              </div>

              {(() => {
                const text = annualReportService.dueDateText || formatDateForDisplay(annualReportService.dueDate);
                const deadlineMatch = text.match(/(.*)\s*\((Final deadline:.*)\)/);

                if (deadlineMatch) {
                  const finalDeadline = deadlineMatch[2].replace('Final deadline:', '').replace(')', '').trim();

                  return (
                    <div className="h-full p-2.5 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 border-2 border-red-500/10 flex flex-col justify-center items-center text-center relative overflow-hidden">
                      <div>
                        <p className="text-xs uppercase font-bold text-red-600 tracking-[0.2em] mb-0.5 animate-pulse">{meta.labelDueDate}</p>
                        <p className="text-sm font-bold text-red-600 dark:text-red-500 leading-none">
                          {finalDeadline}
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="h-full p-2.5 rounded-xl bg-red-600 text-white text-center shadow-lg shadow-red-600/20 flex flex-col justify-center">
                    <p className="text-xs uppercase font-bold tracking-widest opacity-70 mb-0.5">{meta.labelDueDate}</p>
                    <p className="text-base font-bold">{text}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Annual Report Steps Section */}
      {(annualReportService?.state === 'Florida' || annualReportService?.state === 'Wyoming' || annualReportService?.state === 'New Jersey' || annualReportService?.state === 'Delaware') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 animate-fade-in-up" style={{ animationDelay: '100ms', minHeight: '200px' }}>
          <div className="h-full">
            <StepCard 
              step={1} 
              title={getAnnualReportStepTitle(meta, 1)} 
              status={getStepStatus(1)} 
              progressData={getStepProgress(1)}
              onClick={handleStepClick}
            />
          </div>
          <div className="h-full">
            <StepCard 
              step={2} 
              title={getAnnualReportStepTitle(meta, 2)} 
              status={getStepStatus(2)} 
              onClick={() => {}}
            />
          </div>
        </div>
      )}

      {annualReportData && (
        <div className="space-y-6">
          {/* Data Table */}
          <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div
              className="py-3.5 px-5 text-white flex items-center justify-between"
              style={{ background: `linear-gradient(135deg, #4f46e5, #9333ea)` }}
            >
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-bold tracking-tight">{annualReportData.additionalTitle || meta.recordsTableDefaultTitle}</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    {annualReportData.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {annualReportData.rows.filter(row => !row.include).map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-dark-800' : 'bg-gray-50 dark:bg-dark-700/50'}>
                      {annualReportData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">
                          {typeof row[header] === 'number' ? `$${row[header].toLocaleString()}` : row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* Placeholder when no setup exists */}
      {!annualReportService?.dueDate && !annualReportData && !loading && (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div
            className="py-12 px-6 text-center flex flex-col items-center justify-center space-y-4"
            style={{ background: `linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(147, 51, 234, 0.05))` }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 mb-2">
              <FaCalendarAlt className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{meta.emptyStateTitle}</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {meta.emptyStateDescription}
            </p>
            <div className="pt-4">
              <button 
                onClick={fetchAnnualReportData}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-indigo-500/30 transition-all flex items-center gap-2"
              >
                <FaSyncAlt className={isRefreshing ? "animate-spin" : ""} />
                {meta.emptyStateButtonLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnualReportService;

